/**
 * A minimal RFC 4180 CSV writer.
 *
 * Host exports get typed into Excel or Sheets on a phone or a hotel-lobby
 * laptop, and a field with a comma, a quote, or a line break in it — a note
 * like `Vegetarian, no nuts` — has to survive that round trip rather than
 * silently shifting every column after it into the wrong header. That is the
 * whole reason to hand-roll this instead of `row.join(',')`.
 */

/**
 * Leading characters a spreadsheet reads as "evaluate me". A guest name or a
 * note beginning with `=HYPERLINK(...)` would run as a formula the moment the
 * CSV opens; a phone number beginning with `+` would be evaluated to a bare
 * number and lose its plus. A leading apostrophe is the text marker Excel,
 * Sheets and Numbers all honour, and it is hidden on display.
 */
const FORMULA_LEADERS = /^[=+\-@\t\r]/;

/** Quote a single field only when it needs it; double any quote inside it. */
function csvCell(value: unknown): string {
  const raw = value === null || value === undefined ? '' : String(value);
  const text = FORMULA_LEADERS.test(raw) ? `'${raw}` : raw;
  if (/[",\r\n']/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/**
 * Headers plus rows, as one CSV string. CRLF line endings — Excel is the
 * reader that matters most for a host, and it is the pickier of the two about
 * a bare `\n`.
 */
export function toCsv(headers: readonly string[], rows: ReadonlyArray<readonly unknown[]>): string {
  const lines = [headers, ...rows].map((row) => row.map(csvCell).join(','));
  return `${lines.join('\r\n')}\r\n`;
}
